import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Actions from '../Actions';
import { Table, Tag, Space, Statistic, Row, Col, Card, Typography, Empty } from 'antd';

const { Title, Text } = Typography;

interface CompReportViewProps {
  selectedShop?: any;
  compSummary?: any;
  auditLog?: any[];
  compTypes?: any[];
  loading?: boolean;
  actions?: any;
}

function CompReportView({
  selectedShop,
  compSummary,
  auditLog = [],
  compTypes = [],
  loading,
  actions,
}: CompReportViewProps) {
  const shopId = selectedShop?.shopId;
  const [selectedBatchId, setSelectedBatchId] = useState<string>('');

  useEffect(() => {
    if (shopId) {
      actions?.fetchCompTypes(shopId);
    }
  }, [shopId]);

  const handleBatchSearch = () => {
    if (shopId && selectedBatchId) {
      actions?.fetchCompSummary(shopId, selectedBatchId);
      actions?.fetchCompAuditLog(shopId, selectedBatchId);
    }
  };

  const columns = [
    {
      title: 'Order',
      dataIndex: 'orderId',
      key: 'orderId',
      render: (v: string) => <Text code>{v?.slice(-8)}</Text>,
    },
    {
      title: 'Item',
      dataIndex: 'orderItemId',
      key: 'orderItemId',
      render: (v: string) => <Text>{v?.slice(-8)}</Text>,
    },
    {
      title: 'Comp Type',
      dataIndex: 'compTypeCode',
      key: 'compTypeCode',
      render: (v: string) => <Tag color="green">{v}</Tag>,
    },
    {
      title: 'Value',
      dataIndex: 'compValue',
      key: 'compValue',
      render: (v: number) => <Text strong>{v?.toFixed(2)}</Text>,
    },
    {
      title: 'Contact',
      dataIndex: 'contactId',
      key: 'contactId',
      render: (v: string) =>
        v ? (
          <Text type="secondary">{v?.slice(-8)}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Cashier',
      dataIndex: 'cashierId',
      key: 'cashierId',
    },
    {
      title: 'Approved By',
      dataIndex: 'approvedBy',
      key: 'approvedBy',
      render: (v: string) =>
        v ? <Tag color="blue">{v}</Tag> : <Tag>No approval</Tag>,
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) =>
        v ? new Date(v).toLocaleDateString() : '—',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Complementary Report</Title>
      </div>

      {/* Batch filter */}
      <Card style={{ marginBottom: 24 }}>
        <Space>
          <Text strong>Batch ID:</Text>
          <input
            value={selectedBatchId}
            onChange={(e) => setSelectedBatchId(e.target.value)}
            placeholder="Enter batch ID…"
            style={{
              padding: '6px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              width: 280,
            }}
          />
          <button
            onClick={handleBatchSearch}
            style={{
              padding: '6px 16px',
              background: '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Load
          </button>
        </Space>
      </Card>

      {/* Summary */}
      {compSummary && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic title="Total Comps" value={compSummary.count || 0} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Comp Value"
                value={compSummary.totalValue || 0}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#10b981' }}
              />
            </Card>
          </Col>
          {(compSummary.byType || []).map((ct: any) => (
            <Col span={6} key={ct.code}>
              <Card>
                <Statistic
                  title={ct.name}
                  value={ct.value || 0}
                  precision={2}
                  prefix="$"
                />
                <Text type="secondary">{ct.count} items</Text>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Audit log */}
      <Card title="Complementary Audit Log">
        {auditLog.length === 0 && !loading ? (
          <Empty description="No comp records found. Enter a batch ID to search." />
        ) : (
          <Table
            dataSource={auditLog}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 20 }}
            size="small"
          />
        )}
      </Card>
    </div>
  );
}

const mapStateToProps = (state: any) => ({
  selectedShop: state.dashboard.get('selectedShop'),
  compSummary: state.complementary.get('compSummary'),
  auditLog: state.complementary.get('auditLog'),
  compTypes: state.complementary.get('compTypes'),
  loading: state.complementary.get('loading'),
});

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(Actions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompReportView);
